export function toSafeNumber(
  value: string | number | null | undefined,
  fallback = 0
): number {
  if (typeof value === "number")
    return Number.isFinite(value) ? value : fallback
  if (typeof value === "string") {
    const parsed = parseFloat(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

/** Backend party.percent is already 0..100. */
export function normalizePercent(
  value: string | number | null | undefined
): number {
  return Math.max(0, toSafeNumber(value, 0))
}

/** History win_chance comes as 0..1 fraction. */
export function chanceFractionToPercent(chanceFraction: number): number {
  if (!Number.isFinite(chanceFraction) || chanceFraction <= 0) return 0
  return chanceFraction * 100
}

/** Rate (x) derived from chance in percent: x = 100 / chance%. */
export function multiplierFromPercent(
  percent: string | number | null | undefined
): number | null {
  const p = normalizePercent(percent)
  if (p <= 0) return null
  return 100 / p
}

/**
 * Round to hundredths and trim redundant fractional zeros (e.g. "2", "2.1", "2.05").
 * Non-finite input → "0". Used for multipliers, TON stake labels, legit-check rows.
 */
export function formatHundredthsTrimZeros(
  value: string | number | null | undefined
): string {
  const n = toSafeNumber(value, 0)
  if (!Number.isFinite(n)) return "0"
  const rounded = Math.round(n * 100) / 100
  if (rounded === 0) return "0"
  const s = rounded.toFixed(2)
  if (s.endsWith(".00")) return s.slice(0, -3)
  if (s.endsWith("0")) return s.slice(0, -1)
  return s
}

/** x label: up to two decimals; whole values and trailing zeros trimmed (e.g. "2", "2.1", "2.15"). */
export function formatMultiplierDisplay(
  value: number | null | undefined
): string | null {
  if (value == null || !Number.isFinite(value) || value <= 0) return null
  const label = formatHundredthsTrimZeros(value)
  /** Sub-cent multipliers used to stringify to ""; keep winner sheet from showing "x0". */
  return label === "0" ? null : label
}

export function computeRate(prizeTon: number, betTon: number): number {
  if (!Number.isFinite(prizeTon) || !Number.isFinite(betTon) || betTon <= 0)
    return 0
  return prizeTon / betTon
}

export function computeBetFromBankAndChance(
  totalBankTon: number,
  chanceFraction: number
): number {
  if (
    !Number.isFinite(totalBankTon) ||
    !Number.isFinite(chanceFraction) ||
    chanceFraction <= 0
  )
    return 0
  return totalBankTon * chanceFraction
}

/** UI formatting for PvP lists / bank hub: one decimal; whole numbers omit trailing ".0". */
export function formatTonCompact(
  value: string | number | null | undefined
): string {
  const n = toSafeNumber(value, 0)
  const s = n.toFixed(1)
  return s.endsWith(".0") ? s.slice(0, -2) : s
}

/** Round event bubbles: one decimal when needed; whole values without a fractional part. */
export function formatTonBubbleAmount(
  value: string | number | null | undefined
): string {
  const n = toSafeNumber(value, 0)
  const rounded = Math.round(n * 10) / 10
  if (Number.isInteger(rounded)) {
    return String(rounded)
  }
  return rounded.toFixed(1)
}

/** Same rules as {@link formatTonBubbleAmount} (winner sheet, small TON amounts). */
export const formatTonWinner = formatTonBubbleAmount

/**
 * Converts a nanoton integer into a human-readable TON string.
 *
 * Rules:
 * - up to 2 decimals, trailing zeros trimmed (`1.50` → `1.5`, `1.00` → `1`);
 * - values ≥ 1000 are rounded to a whole number to keep pills compact;
 * - returns {@link options.placeholder} (default `"0"`) for non-finite or
 *   non-positive input.
 *
 * Used by gift price pills (inventory grid, gift picker, withdraw confirm).
 */
export function formatTonFromNano(
  nano: number | null | undefined,
  options?: { placeholder?: string; allowZero?: boolean }
): string {
  const placeholder = options?.placeholder ?? "0"
  const allowZero = options?.allowZero ?? false
  if (typeof nano !== "number" || !Number.isFinite(nano)) return placeholder
  if (!allowZero && nano <= 0) return placeholder
  const ton = nano / 1e9
  if (ton >= 1000) return Math.round(ton).toString()
  return ton.toFixed(2).replace(/\.?0+$/, "")
}

export function formatPercentInt(
  percent: string | number | null | undefined
): string {
  return Math.max(0, normalizePercent(percent)).toFixed(0)
}
